<script setup lang="ts">
import { computed, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useI18n } from 'vue-i18n';
import { useStates } from '../router/states';
import authService from '../views/pages/auth/services/authService';

// Boton "Agregar negocio" del menu de negocios: valida requisitos, confirma, crea la empresa adicional
// y cambia a ella. Solo lo ve el usuario principal.
const emit = defineEmits<{ (e: 'click'): void }>();

const { t } = useI18n();
const toast = useToast();
const { userState } = useStates();

const isMainUser = computed(() => (userState?.data?.value as any)?.isMainUser === true);

const showNewCompanyModal = ref(false);
const creatingCompany = ref(false);
const checkingPrerequisites = ref(false);
const certificateWarning = ref<any>(null);

const showIncompleteCompanyModal = ref(false);
const incompleteCompanyDetails = ref<{ companyName: string; companyIdentification: string; missingItems: string[] }>({
  companyName: '',
  companyIdentification: '',
  missingItems: [],
});

const handleCreateNewCompany = async () => {
  emit('click');
  if (!(userState?.data?.value as any)?.subscriptionActive) {
    toast.add({
      severity: 'warn',
      summary: t('appMenu.createCompany'),
      detail: t('appMenu.createCompanyNoSubscription'),
      life: 4000,
    });
    return;
  }
  checkingPrerequisites.value = true;
  certificateWarning.value = null;
  try {
    const result: any = await authService.checkNewCompanyPrerequisites();
    // El backend puede devolver true (todo OK) o {hasCertificateWarning, companyName, companyIdentification}
    if (result && typeof result === 'object' && result.hasCertificateWarning) {
      certificateWarning.value = result;
    }
    showNewCompanyModal.value = true;
  } catch (error: any) {
    if (error && typeof error === 'object' && error.error === 'ERR_AUTH_0008') {
      incompleteCompanyDetails.value = {
        companyName: error.companyName || '',
        companyIdentification: error.companyIdentification || '',
        missingItems: Array.isArray(error.missingItems) ? error.missingItems : [],
      };
      showIncompleteCompanyModal.value = true;
    } else {
      toast.add({
        severity: 'warn',
        summary: t('appMenu.createCompany'),
        detail: typeof error === 'string' ? error : t('shared.errorMessage'),
        life: 5000,
      });
    }
  } finally {
    checkingPrerequisites.value = false;
  }
};

const confirmCreateCompany = async () => {
  creatingCompany.value = true;
  try {
    const createResponse = await authService.createAdditionalCompany();
    showNewCompanyModal.value = false;

    const switchResponse: any = await authService.switchCompany({
      email: (userState?.data?.value as any)?.email || '',
      userId: createResponse.userCompanyId,
      companyId: createResponse.companyId,
    });

    if (switchResponse && switchResponse.token) {
      sessionStorage.setItem('authToken', switchResponse.token);
      toast.add({
        severity: 'success',
        summary: t('appMenu.createCompany'),
        detail: t('appMenu.createCompanySuccess'),
        life: 2000,
      });
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  } catch (error: any) {
    showNewCompanyModal.value = false;
    creatingCompany.value = false;
    toast.add({
      severity: 'error',
      summary: t('appMenu.createCompany'),
      detail: typeof error === 'string' ? error : t('shared.errorMessage'),
      life: 5000,
    });
  }
};
</script>

<template>
  <div v-if="isMainUser" class="new-company-action">
    <Button
      class="w-full"
      icon="pi pi-plus-circle"
      :label="$t('appMenu.createCompany')"
      :loading="checkingPrerequisites"
      @click="handleCreateNewCompany" />
  </div>

  <Dialog
    v-model:visible="showIncompleteCompanyModal"
    :header="$t('appMenu.incompleteCompanyTitle')"
    :modal="true"
    :style="{ width: '460px' }">
    <div class="flex flex-column gap-3">
      <div class="flex align-items-start gap-2">
        <i class="pi pi-exclamation-triangle text-orange-500 mt-1" style="font-size: 1.25rem; flex-shrink: 0"></i>
        <p class="m-0 line-height-3">{{ $t('appMenu.incompleteCompanyMessage') }}</p>
      </div>
      <div class="surface-100 border-round p-3">
        <div class="flex gap-2 mb-1">
          <span class="font-semibold">{{ $t('appMenu.incompleteCompanyName') }}:</span>
          <span>{{ incompleteCompanyDetails.companyName }}</span>
        </div>
        <div class="flex gap-2">
          <span class="font-semibold">{{ $t('appMenu.incompleteCompanyRuc') }}:</span>
          <span>{{ incompleteCompanyDetails.companyIdentification }}</span>
        </div>
      </div>
      <div v-if="incompleteCompanyDetails.missingItems.length > 0">
        <p class="m-0 mb-2 font-semibold">{{ $t('appMenu.incompleteCompanyPending') }}:</p>
        <ul class="m-0 pl-4">
          <li v-for="item in incompleteCompanyDetails.missingItems" :key="item" class="mb-1">
            {{ item }}
          </li>
        </ul>
      </div>
    </div>
    <template #footer>
      <Button :label="$t('shared.understood')" @click="showIncompleteCompanyModal = false" />
    </template>
  </Dialog>

  <Dialog
    v-model:visible="showNewCompanyModal"
    :header="$t('appMenu.createCompanyModalTitle')"
    :modal="true"
    :closable="!creatingCompany"
    :style="{ width: '420px' }">
    <div class="flex flex-column gap-3">
      <div class="flex align-items-start gap-2">
        <i class="pi pi-info-circle text-blue-500 mt-1" style="font-size: 1.25rem; flex-shrink: 0"></i>
        <p class="m-0 line-height-3">{{ $t('appMenu.createCompanyModalMessage') }}</p>
      </div>
      <div
        v-if="certificateWarning"
        class="flex align-items-start gap-2 p-3 border-round"
        style="background: var(--yellow-50); border: 1px solid var(--yellow-300)">
        <i class="pi pi-exclamation-triangle text-yellow-600 mt-1" style="font-size: 1.1rem; flex-shrink: 0"></i>
        <p class="m-0 line-height-3 text-yellow-800">
          {{
            $t('appMenu.createCompanyCertWarning', {
              name: certificateWarning.companyName,
              ruc: certificateWarning.companyIdentification,
            })
          }}
        </p>
      </div>
    </div>
    <template #footer>
      <Button
        :label="$t('shared.cancel')"
        severity="secondary"
        :disabled="creatingCompany"
        @click="showNewCompanyModal = false" />
      <Button :label="$t('shared.accept')" :loading="creatingCompany" @click="confirmCreateCompany" />
    </template>
  </Dialog>
</template>

<style scoped>
.new-company-action {
  border-top: 1px solid var(--surface-border);
  padding: 0.5rem 0.75rem;
}
</style>
